import React, { useCallback } from 'react';
import { Modal, Text, Button, Stack, Group, Alert, Badge } from '@mantine/core';
import { IconTrash, IconX, IconAlertTriangle, IconUser, IconMail } from '@tabler/icons-react';
import { UserListItemDto } from '@/application/dto/UserDtos';
import { useDeleteUserMutation } from '@/application/queries/userQueries';
import { useAuth } from '../../hooks/useAuth';

export interface DeleteUserModalProps {
  isOpen: boolean;
  user: UserListItemDto | null;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * DeleteUserModal - Modal for deleting a user (admin only)
 *
 * Features:
 * - Confirmation dialog with user details
 * - Warning about cascade effects
 * - User summary display (name, email, role, business)
 * - Loading state during deletion
 * - Success/error notifications via mutation hook
 */
export const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
  isOpen,
  user: selectedUser,
  onClose,
  onSuccess
}): React.ReactElement => {
  const { user } = useAuth();
  const deleteUserMutation = useDeleteUserMutation();

  /**
   * Handle delete confirmation
   */
  const handleDelete = useCallback(async (): Promise<void> => {
    if (!selectedUser) {
      return;
    }

    try {
      await deleteUserMutation.mutateAsync({
        id: selectedUser.id,
        userRole: user?.role ?? 'Admin'
      });

      // Close modal and notify parent
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error is handled by mutation hook (notifications)
      console.error('Delete user failed:', error);
    }
  }, [selectedUser, deleteUserMutation, user, onClose, onSuccess]);

  /**
   * Handle cancel
   */
  const handleCancel = useCallback((): void => {
    onClose();
  }, [onClose]);

  if (!selectedUser) {
    return <></>;
  }

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Delete User"
      size="md"
      centered
      closeOnClickOutside={!deleteUserMutation.isPending}
      closeOnEscape={!deleteUserMutation.isPending}
    >
      <Stack gap="md">
        {/* Warning Alert */}
        <Alert icon={<IconAlertTriangle />} title="Warning" color="red" variant="light">
          This action cannot be undone. Deleting this user will permanently remove their account and all associated
          data.
        </Alert>

        {/* User Summary */}
        <Stack gap="sm">
          <Text size="sm" fw={600} c="dimmed">
            User Details
          </Text>

          {/* Full Name / Username */}
          <Group gap="xs">
            <IconUser size={16} color="var(--mantine-color-gray-6)" />
            <Text size="sm">
              <strong>{selectedUser.fullName || selectedUser.userName}</strong>
            </Text>
          </Group>

          {/* Email */}
          <Group gap="xs">
            <IconMail size={16} color="var(--mantine-color-gray-6)" />
            <Text size="sm">{selectedUser.email}</Text>
          </Group>

          {/* Role */}
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              Role:
            </Text>
            {selectedUser.roles.map((role) => (
              <Badge key={role} variant="light" color={role === 'Admin' ? 'red' : 'blue'}>
                {role}
              </Badge>
            ))}
          </Group>

          {/* Business */}
          {selectedUser.businessName && (
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                Business:
              </Text>
              <Text size="sm">
                <strong>{selectedUser.businessName}</strong>
              </Text>
            </Group>
          )}

          {/* Status */}
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              Status:
            </Text>
            <Badge color={selectedUser.isActive ? 'green' : 'gray'} variant="light">
              {selectedUser.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </Group>
        </Stack>

        {/* Cascade Warning */}
        {selectedUser.businessName && (
          <Alert icon={<IconAlertTriangle />} color="orange" variant="light">
            <Text size="sm">
              This user is linked to <strong>{selectedUser.businessName}</strong>. Deleting this user will unlink them
              from the business.
            </Text>
          </Alert>
        )}

        {/* Confirmation Question */}
        <Text size="sm" ta="center" fw={600}>
          Are you sure you want to delete user <strong>{selectedUser.fullName || selectedUser.userName}</strong>?
        </Text>

        {/* Action Buttons */}
        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            leftSection={<IconX size={16} />}
            onClick={handleCancel}
            disabled={deleteUserMutation.isPending}
          >
            Cancel
          </Button>

          <Button
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={handleDelete}
            loading={deleteUserMutation.isPending}
            disabled={deleteUserMutation.isPending}
          >
            Delete User
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
